import type {
  AuditLogDto,
  CreateUserInput,
  DashboardDto,
  ListAuditQuery,
  Paginated,
  SettingsKey,
  SettingsMap,
  SimulateBillingDaysInput,
  SimulateBillingDaysResult,
  UpdateUserInput,
  UserDto,
} from '@almacen/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { LIVE_REFRESH_MS } from '../../lib/stock-queries';

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardDto>('/dashboard'),
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: () => api<SettingsMap>('/settings') });
}

export function useSaveSettings<K extends SettingsKey>(key: K) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: unknown) =>
      api<SettingsMap[K]>(`/settings/${key}`, { method: 'PUT', body: value }),
    onSuccess: async () => {
      await Promise.all(
        ['settings', 'dashboard'].map((root) =>
          queryClient.invalidateQueries({ queryKey: [root] }),
        ),
      );
    },
  });
}

export function simulateBillingDays(
  input: SimulateBillingDaysInput,
): Promise<SimulateBillingDaysResult> {
  return api<SimulateBillingDaysResult>('/settings/billing-rules/simulate', {
    method: 'POST',
    body: input,
  });
}

export function useUsers() {
  return useQuery({ queryKey: ['users'], queryFn: () => api<UserDto[]>('/users') });
}

function useUsersMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useCreateUser() {
  return useUsersMutation((input: CreateUserInput) =>
    api<UserDto>('/users', { method: 'POST', body: input }),
  );
}

export function useUpdateUser() {
  return useUsersMutation(({ id, data }: { id: number; data: UpdateUserInput }) =>
    api<UserDto>(`/users/${id}`, { method: 'PUT', body: data }),
  );
}

export function useResetPassword() {
  return useUsersMutation(({ id, password }: { id: number; password: string }) =>
    api<void>(`/users/${id}/password`, { method: 'POST', body: { password } }),
  );
}

export function useAudit(query: ListAuditQuery) {
  return useQuery({
    queryKey: ['audit', query],
    queryFn: () => api<Paginated<AuditLogDto>>('/audit', { query: { ...query } }),
    placeholderData: keepPreviousData,
  });
}
